import React, { useContext, useState, useEffect, useMemo } from "react";
import { AccountContext } from "./../../store/AccountContext";
import {
  Grid,
  Typography,
  Divider,
  TableContainer,
  Table,
  TableBody,
  TableRow,
  TableCell,
  Button,
  Box,
  CircularProgress,
  FormControlLabel,
  Switch,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useMediaQuery,
  dialogTitleClasses,
  switchClasses,
} from "@mui/material";
import { styled, useTheme } from "@mui/material/styles";
import { Link, useNavigate } from "react-router-dom";
import { v4GridContainerWidthSx } from "../../theme/v4CompatSx";
import usePromise from "react-use-promise";
import visa from "payment-icons/min/flat/visa.svg";
import mastercard from "payment-icons/min/flat/mastercard.svg";
import amex from "payment-icons/min/flat/amex.svg";
import discover from "payment-icons/min/flat/discover.svg";
import CreateIcon from "@mui/icons-material/Create";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import RemoveCircleIcon from "@mui/icons-material/RemoveCircle";
import { useToasts } from "react-toast-notifications";
import Countdown from "react-countdown";
import { useMatch } from "react-router-dom";
import MarketingPreferencesModal from "./MarketingPreferencesModal.js";
import WebTrialCard from "./WebTrialCard";
import { OverviewContext } from "../../store/OverviewContext";
import dayjs from "dayjs";
import advancedFormat from "dayjs/plugin/advancedFormat";
import { useStripe } from "@stripe/react-stripe-js";
import { MEMBER_STATUS_STANDARD } from "../../api/entities/UserProfile";
import { CurrencySymbol } from "@booyaltd/core";
import { GetCustomerFacingDisplayName } from "@booyaltd/core/dist/utils/products";
dayjs.extend(advancedFormat);

/**
 * PM5 T2 (money path, batch "myaccount-profile") — converted from MUI v4 +
 * `makeStyles` to MUI v5+ (`@mui/material`) `styled`/`sx`.
 * See docs/epics/pm5-mui-v5-migration.md §3 (T2) and docs/mui-conversion-guide.md.
 *
 * **SUBSCRIPTIONS/MEMBERSHIP + PAYMENTS ESCALATION CLASS.** This screen owns
 * the auto-renew switch (Stripe cancel/resume), the payment-method summary and
 * the Stripe `confirmCardPayment` re-authentication path. The conversion is
 * styling-only: no handler, member-status branch, Stripe call, condition, prop
 * or DOM node moved. The PM7 Playwright spec
 * (`e2e/membership-autorenew.spec.ts`) drives this page by role and text, not
 * by MUI class names, so nothing in it needed re-pointing.
 *
 * DEAD RULES DROPPED (guide §1.2 row 1) — declared in the v4 `useStyles` object
 * but never referenced from the JSX, so they rendered nothing:
 * `background`, `myAccountContainer`, `myAccountGridContainer`,
 * `myAccountTitle`, `myAccountSection`, `tabsContainer`, `fullWidthTab`,
 * `selected`, `indicator`, `personIconBox`, `personIcon`, `alignLeft`,
 * `backdrop`, `xsTabsContainer`, `xsDropdown`, `xsDropdownItem`, `whiteText`,
 * `iconStyle`, `carrotIcon`, `menuContainer`, `alertSuccessTitle`,
 * `alertWarningTitle`, `headerBox`, `headerBoxTitle`, `autoRenewButton`,
 * `accountStatusButton`, `darkButton`, `buttonMembershipContainerBox`,
 * `smlText`, `ORText`, `joinNowButton`, `freeTrialButton`. (`xsDropdownItem`
 * carried an `&.Mui-selected` selector — dead in v4 and dead now, so there is
 * nothing to re-point at the `FfV5` names.)
 *
 * `classes.tableFirstChild` was referenced by seven `<TableCell>`s but **never
 * declared** — it was `undefined`, so React rendered no class attribute. The
 * `className` is dropped rather than invented.
 */

/* ---------------------------------------------------------------------- *
 * styled() — non-MUI elements that carry a fixed style
 * ---------------------------------------------------------------------- */

// was: useStyles().bodyList — a bare <ul>, which has no `sx` prop (guide §1.2).
const BodyList = styled("ul")(({ theme }) => ({
  paddingLeft: theme.spacing(3),
  fontSize: "1rem",
  fontFamily: "Roboto, Helvetica Neue ,Arial,sans-serif",
  fontWeight: "400",
  lineHeight: "1.2rem",
  "& span": {
    fontWeight: 500,
  },
}));

// was: useStyles().tableText, applied to a bare <li> in the payment-failed
// dialog. Same declarations as `tableTextSx` below, different host element.
const TableTextListItem = styled("li")(({ theme }) => ({
  color: theme.palette.background.ffNavy,
  "& span": {
    fontWeight: 500,
  },
}));

// was: useStyles().cardExpiry — a bare <span>.
const CardExpirySpan = styled("span")({
  fontSize: "0.85rem",
  fontWeight: "400 !important",
});

// was: useStyles().countdownText — a bare <span>. The literal
// `@media (min-width:600px)` becomes `breakpoints.up("sm")`; `themeV5` pins sm
// to the v4 value of 600, so the emitted query is identical.
const CountdownText = styled("span")(({ theme }) => ({
  marginTop: "20px",
  fontSize: "1.4rem",
  lineHeight: "2rem",
  fontWeight: "600 !important",
  [theme.breakpoints.up("sm")]: {
    fontSize: "1.8rem",
    lineHeight: "2.6rem",
  },
}));

// was: useStyles().defaultPaymentMethodCardBrand — a bare <img>.
const CardBrandImg = styled("img")(({ theme }) => ({
  width: "50px",
  maxWidth: "50px",
  paddingRight: theme.spacing(2.5),
}));

// was: useStyles().link — a react-router <Link>, which takes a `className` but
// has no `sx` prop (guide §1.2).
const TextLink = styled(Link)(({ theme }) => ({
  fontWeight: "500 !important",
  color: theme.palette.background.ffNavy,
  textDecoration: "underline",
  "&:hover": {
    cursor: "pointer",
    color: theme.palette.background.ffGreen,
  },
}));

/* ---------------------------------------------------------------------- *
 * sx — styles on MUI components
 * ---------------------------------------------------------------------- */

/**
 * ♦ v4's `<Grid container spacing={2}>` was
 *   `.MuiGrid-spacing-xs-2      { margin: -8px; width: calc(100% + 16px) }`
 *   `.MuiGrid-spacing-xs-2 > .MuiGrid-item { padding: 8px }`
 * v9 replaces that with `gap` on the container and no padding on the items
 * (guide §2). Carry v4's own declarations verbatim rather than re-deriving the
 * arithmetic: omit `spacing` (v9 default 0 ⇒ `gap: 0px`), and give each item
 * the 8px padding itself. Identical by construction — and it matters for the
 * four `<DialogActions>` grids here, whose parent has its own 8px padding that
 * the container used to overhang.
 */
const v4GridContainerSx = { width: "calc(100% + 16px)", margin: "-8px" };
const v4GridItemSx = { padding: "8px" };

// was: useStyles().myAccountSubTitle. The literal `@media (min-width:600px)` /
// `(min-width:960px)` become `breakpoints.up("sm"|"md")`; `themeV5` pins those
// to the v4 values (600/960), so the emitted queries are identical. Every
// `breakpoints.up(...)` below is the same mechanical swap.
const myAccountSubTitleSx = (theme) => ({
  color: theme.palette.background.ffNavy,
  fontSize: "1.4rem",
  textAlign: "left",
  marginTop: theme.spacing(2),
  marginBottom: theme.spacing(2),
  [theme.breakpoints.up("sm")]: {
    fontSize: "1.4rem",
    lineHeight: "1.4rem",
  },
  [theme.breakpoints.up("md")]: {
    fontSize: "1.4rem",
    lineHeight: "1.6rem",
  },
});

// was: useStyles().tableText
const tableTextSx = (theme) => ({
  color: theme.palette.background.ffNavy,
  "& span": {
    fontWeight: 500,
  },
});

// was: useStyles().tableTextLink
const tableTextLinkSx = (theme) => ({
  "& a": {
    color: theme.palette.background.ffNavy,
    textDecoration: "underline",
    "&:hover": {
      color: theme.palette.background.ffGreen,
      cursor: "pointer",
    },
  },
});

// was: useStyles().sectionDivider
const sectionDividerSx = (theme) => ({
  height: "1px",
  backgroundColor: "#c1c1c1",
  margin: theme.spacing(3, 0),
  [theme.breakpoints.up("sm")]: {
    margin: "2% 0",
  },
});

// was: useStyles().alertErrorTitle
const alertErrorTitleSx = (theme) => ({ color: theme.palette.common.white });

// was: useStyles().topMembershipContainer
const topMembershipContainerSx = (theme) => ({
  padding: theme.spacing(3, 4),
  backgroundColor: "#e2e2e2",
  color: theme.palette.background.ffNavy,
  marginBottom: theme.spacing(2),
  [theme.breakpoints.up("sm")]: {
    padding: theme.spacing(4),
  },
  [theme.breakpoints.up("md")]: {
    padding: theme.spacing(6, 4),
  },
});

// was: useStyles().standardStatus / .errorStatus / .warningStatus /
// .expiredStatus — the second of the two classes v4 joined into one
// `className` template literal on the membership panel. They declare only
// `borderLeft`, which `topMembershipContainer` does not, so the merge below is
// conflict-free (guide §2, "Two `makeStyles` keys on one element"); the spread
// order still matches v4's sheet order, status last.
const standardStatusSx = { borderLeft: "10px solid #99ca3c" };
const errorStatusSx = { borderLeft: "10px solid #c53737" };
const warningStatusSx = { borderLeft: "10px solid #ff9800" };
const expiredStatusSx = { borderLeft: "10px solid #ababab" };

// was: useStyles().statusMembershipContainer
const statusMembershipContainerSx = (theme) => ({
  fontSize: "2rem",
  lineHeight: "2.4rem",
  color: theme.palette.background.ffNavy,
  fontWeight: 500,
  marginBottom: theme.spacing(1),
  [theme.breakpoints.up("sm")]: {
    fontSize: "2rem",
    lineHeight: "2.4rem",
  },
  [theme.breakpoints.up("md")]: {
    fontSize: "3rem",
    lineHeight: "3.4rem",
  },
});

// was: useStyles().darkBodyText
const darkBodyTextSx = (theme) => ({
  color: theme.palette.background.ffNavy,
  "& span": {
    fontWeight: 500,
  },
  "& a": {
    color: theme.palette.background.ffNavy,
  },
});

// was: useStyles().buttonMembershipContainer
const buttonMembershipContainerSx = (theme) => ({
  textAlign: "left",
  [theme.breakpoints.up("sm")]: {
    textAlign: "right",
  },
});

// was: useStyles().buttonMembership, plus a v4 default that has to be restored.
//
// PARITY RESTORE (guide §2, "Button default color"): `color: "text.primary"` is
// NOT in the v4 rule. v4's plain <Button> defaulted to `color="default"`, whose
// root style was `color: theme.palette.text.primary`. v5+ deleted `default` and
// made `primary` the default, which would paint the label lime on the lime
// `typography.button` background — i.e. invisible "Turn Off Auto Renew" /
// "Join Now" / "Complete Authentication" buttons on the membership panel. The
// `variant="outlined"` uses need it too (themeV5's `outlined` slot only
// restates `color` on `:hover`).
const buttonMembershipSx = (theme) => ({
  width: "100%",
  margin: "12px 0% 0 0%",
  [theme.breakpoints.up("sm")]: {
    textAlign: "center",
    width: "240px",
    fontSize: "1rem",
  },
  color: "text.primary",
});

// was: useStyles().dialogButton (+ the same default-colour restore)
const dialogButtonSx = (theme) => ({
  width: "96%",
  margin: "10px 2% 5px 2%",
  [theme.breakpoints.up("sm")]: {
    width: "48.2%",
    margin: "10px 5px",
  },
  color: "text.primary",
});

// was: useStyles().dialogButtonSml (+ the same default-colour restore)
const dialogButtonSmlSx = (theme) => ({
  width: "96%",
  margin: "10px 2% 5px 2%",
  [theme.breakpoints.up("sm")]: {
    width: "47.2%",
    margin: "10px 5px",
  },
  color: "text.primary",
});

/**
 * was: useStyles().autoRenewSwitch — the §1.2b hit this batch was flagged for.
 *
 * All four v4 selectors were hand-written global `.MuiSwitch-*` strings that
 * reach into the `<Switch>` this file renders. Both convert here, so the
 * `FfV5` prefix would make them stop matching, silently — the switch would
 * snap back to MUI's stock 58×38 geometry with a 20px throw, and nothing in
 * lint, tsc or jsdom would say so. The names are read off `switchClasses`
 * instead.
 *
 * Note `switchClasses.checked` is literally `Mui-checked`, NOT `FfV5Switch-…`:
 * the global *state* slots keep the `Mui` prefix under a custom class-name
 * generator (guide §2, "State slots are global `Mui-*`"). Compounding it with
 * `switchClasses.switchBase` reproduces v4's selector exactly — same element,
 * same (0,3,0) specificity that beat MUI's own `translateX(20px)` rule.
 */
const autoRenewSwitchSx = {
  [`& .${switchClasses.root}`]: {
    width: "74px",
    height: "44px",
  },
  [`& .${switchClasses.thumb}`]: {
    width: "26px",
    height: "26px",
  },
  [`& .${switchClasses.switchBase}.${switchClasses.checked}`]: {
    transform: "translateX(30px)",
  },
  [`& .${switchClasses.track}`]: {
    borderRadius: "10px",
  },
};

/**
 * was: useStyles().autoRenewDialog — a §1.2b hit on the Dialog root, reaching
 * into the DialogTitle it renders. Re-pointed at `dialogTitleClasses.root`
 * (`FfV5DialogTitle-root`) rather than the hand-written v4 string.
 */
const autoRenewDialogSx = {
  [`& .${dialogTitleClasses.root}`]: {
    backgroundColor: "#171717",
  },
};

/**
 * was: useStyles().autoRenewDialogTitle, whose rule was
 * `"& .MuiTypography-root": { lineHeight: "1.6rem", color: "#eee" }`.
 *
 * ♦ NEW DELTA (see guide §2). In v4 `DialogTitle` rendered
 * `<div class="MuiDialogTitle-root"><h2 class="MuiTypography-root">…</h2></div>`,
 * so that DESCENDANT selector styled the inner Typography. In v5+ `DialogTitle`
 * **is** `styled(Typography)` — the h2 is the root itself, there is no
 * descendant Typography, and the ported selector would match nothing. The two
 * declarations therefore move onto the DialogTitle's own `sx`: same element,
 * same rendering.
 */
const autoRenewDialogTitleSx = {
  lineHeight: "1.6rem",
  color: "#eee",
};

// was: useStyles().upgradeMembershipContainer
const upgradeMembershipContainerSx = (theme) => ({
  padding: theme.spacing(3, 4),
  backgroundColor: theme.palette.background.upgrade,
  color: "#fff",
  borderLeft: "10px solid #0f98d4",
  [theme.breakpoints.up("sm")]: {
    padding: theme.spacing(4),
  },
  [theme.breakpoints.up("md")]: {
    padding: theme.spacing(2, 4),
  },
});

// was: useStyles().upgradeMembershipTextContainer
const upgradeMembershipTextContainerSx = {
  display: "flex",
  alignItems: "center",
};

// was: useStyles().upgradeMembershipText
const upgradeMembershipTextSx = { letterSpacing: "-0.5px" };

// was: useStyles().upgradeMembershipButtonContainer
const upgradeMembershipButtonContainerSx = (theme) => ({
  textAlign: "center",
  [theme.breakpoints.up("sm")]: {
    textAlign: "right",
  },
  "& a": {
    textDecoration: "none",
  },
});

// was: useStyles().upgradeMemberShipButton. This one pins its own `color`, so
// the v5+ Button default-colour change cannot reach its label — no restore.
const upgradeMemberShipButtonSx = (theme) => ({
  backgroundColor: "#0f98d4",
  color: "#fff",
  border: "2px solid #0f98d4",
  width: "100%",
  marginTop: theme.spacing(1),
  [theme.breakpoints.up("sm")]: {
    textAlign: "right",
    width: "auto",
    marginTop: theme.spacing(0),
  },
  "&:hover": {
    backgroundColor: "#109edb",
    border: "2px solid #109edb",
  },
});

// was: useStyles().alertErrorContainer
const alertErrorContainerSx = (theme) => ({
  padding: theme.spacing(3, 4),
  backgroundColor: theme.palette.error.main,
  color: "#fff",
  borderLeft: "10px solid #c53737",
  marginBottom: theme.spacing(2),
  [theme.breakpoints.up("sm")]: {
    padding: theme.spacing(4),
  },
  [theme.breakpoints.up("md")]: {
    padding: theme.spacing(2, 4),
  },
});

export default function Overview() {
  const { addToast } = useToasts();
  const navigate = useNavigate();
  // v6 has no standalone <Route>; the URL-driven modal renders on match
  const marketingPrefsMatch = useMatch("/myaccount/overview/marketing-prefs");
  const theme = useTheme();
  // ♦ `breakpoints.down(key)` flipped meaning between v4 and v5+ (guide §2).
  // v4's `down("sm")` meant "up to the START of the next breakpoint", i.e.
  // `max-width: 959.95px`; in v5+ the same query is `down("md")`. Copying the
  // key across unchanged would have moved the Dialog full-screen threshold from
  // 960px down to 600px — every tablet-width auto-renew confirmation would stop
  // going full screen. The key is re-derived, not copied.
  const fullScreen = useMediaQuery(theme.breakpoints.down("md"));
  /**
   * ♦ v4's `<Hidden only="xs">` / `<Hidden smUp>` pairs (12 of them, in the
   * personal-details and payment-details tables) were driven by a SINGLE
   * `width` value from `withWidth()`, so exactly one branch ever rendered —
   * including where no media query can be evaluated (jsdom, prerender), where
   * `width` falls through to `xs` and only the narrow branch renders. Two
   * independent `useMediaQuery` calls would each return `false` there and
   * render BOTH branches (guide §2, "Complementary `<Hidden>` pairs"). One
   * query, both booleans derived from it, reproduces v4 at every width.
   *
   * Polarity: `only="xs"` hides when width === xs, so it renders when `smUp`;
   * `smUp` hides at sm and above, so it renders when `!smUp`.
   */
  const smUp = useMediaQuery(theme.breakpoints.up("sm"));
  const stripe = useStripe();

  // Account Context
  const {
    updating,
    userProfile,
    getMembershipDetails,
    handleCancelMembership,
    handleResumeMembership,
    subscriptionProducts,
    getSubscriptionProducts,
  } = useContext(AccountContext);

  const { overview } = useContext(OverviewContext);

  useEffect(() => {
    getSubscriptionProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [reloadTrigger, setReloadTrigger] = useState(Date.now());
  const [membershipDetails, , state] = usePromise(getMembershipDetails, [
    reloadTrigger,
  ]);

  const loading = state === "pending";

  const standardProduct = useMemo(() => {
    return subscriptionProducts?.results.find(
      (p) => p.memberStatusId === "standard",
    );
  }, [subscriptionProducts]);

  // eslint-disable-next-line
  const reload = () => setReloadTrigger(Date.now());

  // Auto Renew Logic
  // eslint-disable-next-line
  const [autoRenewChecked, setAutoRenewChecked] = useState();

  const [paymentFailedInfoOpen, setPaymentFailedInfoOpen] = useState(false);
  const [paymentAlreadyUpdatedInfoOpen, setPaymentAlreadyUpdatedInfoOpen] =
    useState(false);
  const [turnOffAutoRenewOpen, setTurnOffAutoRenewOpen] = useState(false);
  const [turnOnAutoRenewOpen, setTurnOnAutoRenewOpen] = useState(false);

  const [paymentAuthStatus, setPaymentAuthStatus] = useState("");

  const handleAutoRenewSwitch = () => {
    if (!membershipDetails || !membershipDetails.hasStripeSubscription) {
      navigate("/myaccount/join-now");
      return;
    }

    if (
      membershipDetails &&
      !membershipDetails.defaultPaymentMethod &&
      membershipDetails.subscription.cancelAtPeriodEnd
    ) {
      navigate("/myaccount/add-payment-method-autorenew");
      return;
    }

    if (
      membershipDetails &&
      membershipDetails.hasStripeSubscription &&
      membershipDetails.subscription.cancelAtPeriodEnd
    ) {
      // Show Turn On Auto Renew Dialog (Modal)
      setTurnOnAutoRenewOpen(true);
    } else {
      // Show Turn Off Auto Renew Dialog (Modal)
      setTurnOffAutoRenewOpen(true);
    }
  };

  // Grand
  const handleTurnOffAutoRenew = async () => {
    try {
      await handleCancelMembership();
      // Add Toast - Success
      addToast("Auto Renew Turned Off", {
        appearance: "success",
      });
      setAutoRenewChecked(false);
      handleAutoRenewOffCloseDialog();
      reload();
    } catch (e) {
      addToast(e.toString(), {
        appearance: "error",
      });
    }
  };

  // Grand
  const handleTurnOnAutoRenew = async () => {
    try {
      await handleResumeMembership();
      addToast("Auto Renew Turned On.", {
        appearance: "success",
      });
      setAutoRenewChecked(true);
      handleAutoRenewOnCloseDialog();
      reload();
    } catch (e) {
      addToast(e.toString(), {
        appearance: "error",
      });
    }
  };

  const handleAutoRenewOffCloseDialog = () => {
    setTurnOffAutoRenewOpen(false);
  };

  const handleAutoRenewOnCloseDialog = () => {
    setTurnOnAutoRenewOpen(false);
  };

  const handlePaymentFailedCloseDialog = () => {
    setPaymentFailedInfoOpen(false);
  };

  const handlePaymentAlreadyUpdatedCloseDialog = () => {
    setPaymentAlreadyUpdatedInfoOpen(false);
  };

  const handlePaymentAuthCloseDialog = () => {
    reload();
    setPaymentAuthStatus({ dialogOpen: false });
  };

  const countdownRenderer = ({
    days,
    hours,
    minutes,
    seconds,
    completed,
    props: { endingText, endedText },
  }) => {
    if (completed) {
      setTimeout(() => {
        window.location.reload();
      }, 2000);

      return (
        <Typography variant="body1" sx={tableTextSx}>
          {endedText}
        </Typography>
      );
    } else {
      // Render countdown
      return (
        <Typography
          variant="body1"
          sx={tableTextSx}
          style={{ marginTop: "8px", fontWeight: 500 }}
        >
          {endingText} <br />
          <CountdownText>
            {days >= 1 && (
              <>
                {days.toString().length === 1 ? "" : ""}
                {days}
                <span>d</span>
                <span style={{ fontWeight: "bold", color: "#888" }}>{":"}</span>
              </>
            )}
            {hours.toString().length === 1 ? "0" : ""}
            {hours}
            <span>h</span>
            <span style={{ fontWeight: "bold", color: "#888" }}>{":"}</span>
            {minutes.toString().length === 1 ? "0" : ""}
            {minutes}
            <span>m</span>
            <span style={{ fontWeight: "bold", color: "#888" }}>{":"}</span>
            {seconds.toString().length === 1 ? "0" : ""}
            {seconds}
            <span>s</span>
          </CountdownText>
        </Typography>
      );
    }
  };

  const RenderAnnualUpgradeCountdown = ({ days, hours, minutes, seconds }) => {
    return (
      <span>
        <span>{days}d</span> :{" "}
        <span>
          {hours.toString().length === 1 ? "0" : ""}
          {hours}h
        </span>{" "}
        :{" "}
        <span>
          {minutes.toString().length === 1 ? "0" : ""}
          {minutes}m
        </span>{" "}
        :{" "}
        <span>
          {seconds.toString().length === 1 ? "0" : ""}
          {seconds}s
        </span>
      </span>
    );
  };

  function PrettyMembershipHeader(membershipDetails) {
    if (membershipDetails) {
      switch (membershipDetails.memberStatusId) {
        case "standard":
          if (membershipDetails.accountAction) {
            if (
              membershipDetails.accountAction.action === "authorize-payment"
            ) {
              return PrettyMembershipDetail(
                "Action Required",
                "Your FarmFLiX Membership Payment requires Authorization.",
                "Authorize your Payment Method to maintain your membership.",
              );
            }
            if (
              membershipDetails.accountAction.action === "add-payment-method"
            ) {
              if (membershipDetails.defaultPaymentMethod) {
                return PrettyMembershipDetail(
                  "Payment Required",
                  "Your FarmFLiX Membership requires payment.",
                  "Update your Payment Method to start watching.",
                );
              } else {
                return PrettyMembershipDetail(
                  "Payment Required",
                  "Your FarmFLiX Membership requires payment.",
                  "Add a Payment Method to start watching.",
                );
              }
            }
          } else {
            return PrettyMembershipDetail(
              "Active",
              "Your FarmFLiX Membership is Active",
            );
          }
          break;
        case "standard-expired":
          if (membershipDetails.accountAction) {
            if (
              membershipDetails.accountAction.action === "authorize-payment"
            ) {
              return PrettyMembershipDetail(
                "Action Required",
                "Your FarmFLiX Membership Payment requires Authorization.",
                "Authorize your Payment Method to start watching.",
              );
            } else if (
              membershipDetails.accountAction.action === "add-payment-method"
            ) {
              return PrettyMembershipDetail(
                "Payment Required",
                "Your FarmFLiX Membership requires payment",
                "Add a Payment Method to start watching.",
              );
            }
          } else {
            return PrettyMembershipDetail(
              "Lapsed",
              "Your FarmFLiX Membership has Expired!",
              "Start watching again in two simple steps.",
            );
          }
          break;
        case "standard-incomplete":
          return PrettyMembershipDetail(
            "Free account",
            "Watch our free episodes in the FarmFLiX app.",
            "Join to watch every episode.",
          );
        case "trial":
          return PrettyMembershipDetail(
            "Trial",
            "Don't miss out! Join now to maintain your FarmFLiX Membership.",
          );
        case "new-trial-eligible":
          return PrettyMembershipDetail(
            "Take Another Free Trial",
            "Find out what's changed since you were last here",
          );
        case "trial-expired":
          return PrettyMembershipDetail(
            "Trial Expired",
            "Your Free Trial has Expired!",
            "Start watching again in two simple steps.",
          );
        case "locked":
          if (overview && overview.doors.open) {
            return PrettyMembershipDetail(
              "Membership Expired",
              "Your membership access has expired!",
            );
          } else {
            return PrettyMembershipDetail(
              "Membership Locked",
              "Your Membership is Locked",
            );
          }

        default:
      }
    }
  }

  const PrettyMembershipDetail = (title, subtitle, desc) => {
    return (
      <>
        <Typography variant="h4" sx={statusMembershipContainerSx}>
          {title}
        </Typography>
        {subtitle && (
          <Typography
            variant="body1"
            sx={darkBodyTextSx}
            style={{ marginBottom: "12px" }}
          >
            {subtitle}
          </Typography>
        )}
        {desc && (
          <>
            <Typography
              variant="body1"
              sx={darkBodyTextSx}
              style={{ marginBottom: "12px" }}
            >
              <span>{desc}</span>
            </Typography>
          </>
        )}
      </>
    );
  };

  const MemberStatusBox = (props) => {
    const attemptAuthentication = () => {
      console.log("Attempt Authentication...");

      if (membershipDetails && membershipDetails.accountAction) {
        stripe
          .confirmCardPayment(membershipDetails.accountAction.clientSecret, {
            payment_method: membershipDetails.accountAction.paymentMethodId,
          })
          .then((result) => {
            if (result.error) {
              console.log("Payment Auth failure...", result);
              setPaymentAuthStatus({
                dialogOpen: true,
                status: "auth-failure",
              });
              return;
            } else {
              if (result.paymentIntent.status === "succeeded") {
                console.log("Payment Auth success...");
                setPaymentAuthStatus({
                  dialogOpen: true,
                  status: "auth-success",
                });
                return;
              }
            }
          })
          .catch((error) => {
            console.log(error);
            setPaymentAuthStatus({
              dialogOpen: true,
              status: "payment-failure",
            });
            return;
          });
      }
    };

    function UpgradeBanner() {
      /*
        Standard
        Monthly product only
        Doesn't matter about auto renew
        Limited time only
        Button to upgrade page
      */
      return (
        <>
          {overview && membershipDetails && (
            <>
              {membershipDetails.memberStatusId === MEMBER_STATUS_STANDARD &&
                membershipDetails.productId.includes("monthly") &&
                membershipDetails.hasStripeCustomer &&
                overview.doors.annualUpgradeOpen && (
                  <Grid container sx={upgradeMembershipContainerSx}>
                    <Grid
                      size={{ xs: 12, sm: 9 }}
                      sx={upgradeMembershipTextContainerSx}
                    >
                      <Typography variant="body1" sx={upgradeMembershipTextSx}>
                        <b>Save 30% -</b> Upgrade to an Annual Plan Now. Limited
                        Time Only. <br />
                        {overview.doors.annualUpgradeOpenUntil &&
                          overview.doors.annualUpgradeOpenUntil >
                            dayjs().toDate() && (
                            <b style={{ fontSize: "1.2rem", marginTop: "6px" }}>
                              <Countdown
                                date={overview.doors.annualUpgradeOpenUntil}
                                renderer={RenderAnnualUpgradeCountdown}
                                zeroPadTime={1}
                              />
                            </b>
                          )}
                      </Typography>
                    </Grid>
                    <Grid
                      size={{ xs: 12, sm: 3 }}
                      sx={upgradeMembershipButtonContainerSx}
                    >
                      <Link to="/myaccount/annual-upgrade">
                        <Button size="small" sx={upgradeMemberShipButtonSx}>
                          {userProfile.productAutoRenewing
                            ? "Upgrade"
                            : "Turn on Auto Renew"}
                        </Button>
                      </Link>
                    </Grid>
                  </Grid>
                )}
            </>
          )}
        </>
      );
    }

    function CardExpiredBanner() {
      let cardExpiry = null;
      if (membershipDetails && membershipDetails.defaultPaymentMethod) {
        cardExpiry = dayjs(
          `${membershipDetails.defaultPaymentMethod.exp.year}-${membershipDetails.defaultPaymentMethod.exp.month}-1`,
        ).endOf("month");
      }

      return (
        <>
          {cardExpiry && dayjs().isAfter(cardExpiry) && (
            <Grid container sx={alertErrorContainerSx}>
              <Typography variant="body1" sx={alertErrorTitleSx}>
                <b>Your current payment method has expired.</b> Please update
                your payment details below.
              </Typography>
            </Grid>
          )}
        </>
      );
    }

    // was: the `${classes.topMembershipContainer} ${…ternary…}` template
    // literal. Same ternary chain, same order, now yielding style objects.
    const statusBorderSx =
      membershipDetails.memberStatusId === "standard" &&
      !membershipDetails.accountAction
        ? standardStatusSx
        : membershipDetails.memberStatusId === "standard" &&
            membershipDetails.accountAction // Payment Failed / Auth Required
          ? errorStatusSx
          : membershipDetails.memberStatusId === "standard-expired" &&
              !membershipDetails.accountAction // Expired
            ? expiredStatusSx
            : membershipDetails.memberStatusId === "standard-expired" &&
                membershipDetails.accountAction // Payment Failed / Auth Required
              ? errorStatusSx
              : membershipDetails.memberStatusId === "standard-incomplete"
                ? warningStatusSx
                : membershipDetails.memberStatusId === "trial"
                  ? standardStatusSx
                  : membershipDetails.memberStatusId === "trial-expired"
                    ? warningStatusSx
                    : membershipDetails.memberStatusId === "locked" &&
                        overview &&
                        !overview.doors.open
                      ? errorStatusSx
                      : membershipDetails.memberStatusId === "locked" &&
                          overview &&
                          overview.doors.open
                        ? warningStatusSx
                        : {};

    return (
      <>
        <UpgradeBanner />
        <CardExpiredBanner />
        <Grid
          container
          sx={(theme) => ({
            ...topMembershipContainerSx(theme),
            ...statusBorderSx,
          })}
        >
          <Grid size={{ xs: 12, sm: 8 }}>
            {/* Membershup Pretty Title */}
            {PrettyMembershipHeader(membershipDetails)}

            {/* Stripe Membership Current Plan  */}
            {membershipDetails.memberStatusId === "standard" && (
              <>
                {membershipDetails.hasStripeSubscription ? (
                  <>
                    {membershipDetails.subscription && (
                      <>
                        {!membershipDetails.subscription.cancelAtPeriodEnd ? (
                          <>
                            {membershipDetails.subscription.nextPayment && (
                              <Typography
                                variant="body1"
                                sx={darkBodyTextSx}
                                style={{ marginBottom: "12px" }}
                              >
                                {membershipDetails.subscription.status ===
                                "past_due"
                                  ? "We'll try to collect payment again on: "
                                  : "Next Payment:"}
                                <span>
                                  {" "}
                                  {dayjs(
                                    membershipDetails.subscription.nextPayment,
                                  ).format("Do MMMM YYYY")}
                                </span>
                              </Typography>
                            )}
                          </>
                        ) : (
                          <>
                            <Typography
                              variant="body1"
                              sx={darkBodyTextSx}
                              style={{ marginTop: "12px" }}
                            >
                              Membership will expire on:{" "}
                              <span>
                                {dayjs(
                                  userProfile.subscriptionExpiryDate,
                                ).format("Do MMMM YYYY")}
                              </span>
                            </Typography>
                          </>
                        )}
                      </>
                    )}
                    <Typography variant="body1" sx={darkBodyTextSx}>
                      Current Plan:{" "}
                      <span>
                        {GetCustomerFacingDisplayName(
                          `${membershipDetails?.productId ?? ""}`,
                        )}
                        {/* {!membershipDetails.subscription.collectionPaused && (
                          <>
                            {" / "}
                            {CurrencySymbol({
                              currency: membershipDetails.currency
                            })}
                            {(
                              membershipDetails.subscription.cost / 100
                            ).toFixed(2)}{" "}
                          </>
                        )} */}
                      </span>
                    </Typography>
                  </>
                ) : (
                  <>
                    <Typography variant="body1" sx={darkBodyTextSx}>
                      Current Plan:{" "}
                      <span>
                        {GetCustomerFacingDisplayName(
                          `${membershipDetails?.productId ?? ""}`,
                        )}
                      </span>
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={darkBodyTextSx}
                      style={{ marginTop: "12px" }}
                    >
                      Membership will expire on:{" "}
                      <span>
                        {dayjs(userProfile.subscriptionExpiryDate).format(
                          "Do MMMM YYYY",
                        )}
                      </span>
                    </Typography>
                  </>
                )}
              </>
            )}

            {(membershipDetails.memberStatusId === "standard-expired" ||
              membershipDetails.memberStatusId === "locked") &&
              userProfile.subscriptionExpiryDate && (
                <>
                  <Typography
                    variant="body1"
                    sx={darkBodyTextSx}
                    style={{ marginTop: "12px" }}
                  >
                    Membership expired:{" "}
                    <span>
                      {dayjs(userProfile.subscriptionExpiryDate).format(
                        "Do MMMM YYYY",
                      )}
                    </span>
                  </Typography>
                </>
              )}

            {/* Handle Trial / Trial Expired Countdown(s) */}
            {membershipDetails.memberStatusId === "trial" && (
              <>
                <Countdown
                  date={Date.parse(userProfile.subscriptionExpiryDate)}
                  renderer={countdownRenderer}
                  endingText={
                    membershipDetails.memberStatusId === "trial"
                      ? "Your Free Trial ends in:"
                      : "We're giving you another chance to join FarmFLiX.  Don't miss out!"
                  }
                  endedText={
                    membershipDetails.memberStatusId === "trial"
                      ? "Your Free Trial has Ended."
                      : ""
                  }
                  zeroPadTime={2}
                  daysInHours={true}
                />
              </>
            )}
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }} sx={buttonMembershipContainerSx}>
            {membershipDetails.memberStatusId === "standard" ||
            membershipDetails.memberStatusId === "standard-expired" ? (
              <>
                {membershipDetails.hasStripeCustomer ? (
                  <>
                    {membershipDetails.hasStripeSubscription ? (
                      <>
                        {membershipDetails.accountAction ? (
                          <>
                            {membershipDetails.accountAction.action ===
                              "authorize-payment" && (
                              <>
                                <Button
                                  onClick={attemptAuthentication}
                                  size="large"
                                  sx={buttonMembershipSx}
                                >
                                  Complete Authentication
                                </Button>
                              </>
                            )}
                            {membershipDetails.accountAction.action ===
                              "add-payment-method" && (
                              <>
                                <Link
                                  to={
                                    membershipDetails.defaultPaymentMethod
                                      ? "/myaccount/payment-method/update"
                                      : "/myaccount/payment-method/add"
                                  }
                                  style={{ textDecoration: "none" }}
                                >
                                  <Button size="large" sx={buttonMembershipSx}>
                                    {membershipDetails.defaultPaymentMethod
                                      ? "Update"
                                      : "Add"}{" "}
                                    Payment
                                  </Button>
                                </Link>
                              </>
                            )}
                          </>
                        ) : (
                          <>
                            <Button
                              onClick={handleAutoRenewSwitch}
                              sx={buttonMembershipSx}
                            >
                              {membershipDetails.memberStatusId === "standard"
                                ? "Turn " +
                                  (membershipDetails.subscription
                                    .cancelAtPeriodEnd
                                    ? "On"
                                    : "Off") +
                                  " Auto Renew"
                                : "Join Now"}
                            </Button>
                          </>
                        )}
                      </>
                    ) : (
                      <Button
                        onClick={handleAutoRenewSwitch}
                        sx={buttonMembershipSx}
                      >
                        {membershipDetails.memberStatusId === "standard"
                          ? "Turn On Auto Renew"
                          : "Join Now"}
                      </Button>
                    )}
                  </>
                ) : (
                  <Button
                    onClick={handleAutoRenewSwitch}
                    sx={buttonMembershipSx}
                  >
                    {membershipDetails.memberStatusId === "standard"
                      ? "Turn On Auto Renew"
                      : "Join Now"}
                  </Button>
                )}
                {membershipDetails.memberStatusId === "standard" &&
                  !membershipDetails.hasStripeSubscription && (
                    <Typography
                      variant="body2"
                      sx={darkBodyTextSx}
                      style={{ marginTop: "12px" }}
                    >
                      Billing will not commence until your Giftcard expires on{" "}
                      <span>
                        {dayjs(userProfile.subscriptionExpiryDate).format(
                          "Do MMMM YYYY",
                        )}
                      </span>
                    </Typography>
                  )}
              </>
            ) : membershipDetails.memberStatusId === "trial" ||
              membershipDetails.memberStatusId === "trial-expired" ? (
              <>
                {standardProduct && (
                  <Link
                    to="/myaccount/join-now"
                    style={{ textDecoration: "none" }}
                  >
                    <Button size="large" sx={buttonMembershipSx}>
                      Join Now
                    </Button>
                  </Link>
                )}
              </>
            ) : membershipDetails.memberStatusId === "standard-incomplete" ? (
              <>
                {membershipDetails.hasStripeCustomer ? (
                  <>
                    {membershipDetails.hasStripeSubscription ? (
                      <>
                        {membershipDetails.accountAction ? (
                          <>
                            {membershipDetails.accountAction.action ===
                              "authorize-payment" && (
                              <>
                                <Button
                                  onClick={attemptAuthentication}
                                  size="large"
                                  sx={buttonMembershipSx}
                                >
                                  Complete Authentication
                                </Button>
                              </>
                            )}
                            {membershipDetails.accountAction.action ===
                              "add-payment-method" && (
                              <>
                                <Link
                                  to="/myaccount/payment-method/update"
                                  style={{ textDecoration: "none" }}
                                >
                                  <Button size="large" sx={buttonMembershipSx}>
                                    Update Payment
                                  </Button>
                                </Link>
                              </>
                            )}
                          </>
                        ) : (
                          <>
                            <Button
                              onClick={handleAutoRenewSwitch}
                              sx={buttonMembershipSx}
                            >
                              Turn{" "}
                              {membershipDetails.subscription.cancelAtPeriodEnd
                                ? "On"
                                : "Off"}{" "}
                              Auto Renew
                            </Button>
                          </>
                        )}
                      </>
                    ) : (
                      <>
                        {standardProduct && (
                          <Link
                            to="/myaccount/join-now"
                            style={{ textDecoration: "none" }}
                          >
                            <Button size="large" sx={buttonMembershipSx}>
                              Join Now
                            </Button>
                          </Link>
                        )}
                      </>
                    )}
                  </>
                ) : (
                  <>
                    <Button
                      onClick={handleAutoRenewSwitch}
                      sx={buttonMembershipSx}
                    >
                      {membershipDetails.memberStatusId === "standard"
                        ? "Turn On Auto Renew"
                        : "Join Now"}
                    </Button>
                  </>
                )}
              </>
            ) : membershipDetails.memberStatusId === "new-trial-eligible" ? (
              <>
                <Button
                  onClick={handleAutoRenewSwitch}
                  variant="outlined"
                  sx={buttonMembershipSx}
                >
                  Join Now
                </Button>
              </>
            ) : membershipDetails.memberStatusId === "locked" ? (
              <>
                {overview && overview.doors.open ? (
                  <Link
                    to="/myaccount/join-now"
                    style={{ textDecoration: "none" }}
                  >
                    <Button size="large" sx={buttonMembershipSx}>
                      Join Now
                    </Button>
                  </Link>
                ) : (
                  ""
                )}
              </>
            ) : (
              ""
            )}
          </Grid>
        </Grid>
      </>
    );
  };

  return (
    <>
      {/* Turn Off Auto Renew */}
      {userProfile &&
        membershipDetails &&
        membershipDetails.subscription &&
        !membershipDetails.subscription.cancelAtPeriodEnd && (
          <>
            <Dialog
              fullScreen={fullScreen}
              onClose={handleAutoRenewOffCloseDialog}
              aria-labelledby="customized-dialog-title"
              open={turnOffAutoRenewOpen}
              sx={autoRenewDialogSx}
            >
              <DialogTitle
                id="customized-dialog-title"
                onClose={handleAutoRenewOffCloseDialog}
                sx={autoRenewDialogTitleSx}
              >
                Turn Off Auto Renew
              </DialogTitle>
              <DialogContent dividers>
                <Typography variant="body1" sx={tableTextSx}>
                  By turning off Auto Renew, you will:
                  <br />
                </Typography>
                <BodyList>
                  <li>
                    still have access to FarmFLiX for the remainder of your
                    billing period, to{" "}
                    <span>
                      {userProfile.subscriptionExpiryDate &&
                        new Intl.DateTimeFormat("en-GB", {
                          year: "numeric",
                          month: "long",
                          day: "2-digit",
                        }).format(new Date(userProfile.subscriptionExpiryDate))}
                    </span>
                    . After this period, you will no longer has access to
                    FarmFLiX content.
                  </li>
                  <li>
                    no longer be charged after your billing period has elapsed.
                  </li>
                </BodyList>
                <Typography variant="body1" sx={tableTextSx}>
                  <span>Are you sure you wish to turn off Auto Renew?</span>
                </Typography>
              </DialogContent>
              <DialogActions>
                <Grid container sx={v4GridContainerSx}>
                  <Grid size={{ xs: 12 }} sx={v4GridItemSx}>
                    <Button
                      onClick={handleTurnOffAutoRenew}
                      disabled={updating}
                      sx={dialogButtonSx}
                    >
                      Yes, Turn Off
                    </Button>
                    <Button
                      onClick={handleAutoRenewOffCloseDialog}
                      sx={dialogButtonSx}
                      variant="outlined"
                    >
                      No, Go Back
                    </Button>
                  </Grid>
                </Grid>
              </DialogActions>
            </Dialog>
          </>
        )}

      {/* Turn On Auto Renew */}
      {userProfile &&
        membershipDetails &&
        membershipDetails.subscription &&
        membershipDetails.subscription.cancelAtPeriodEnd && (
          <>
            <Dialog
              fullScreen={fullScreen}
              onClose={handleAutoRenewOnCloseDialog}
              aria-labelledby="customized-dialog-title"
              open={turnOnAutoRenewOpen}
              sx={autoRenewDialogSx}
            >
              <DialogTitle
                id="customized-dialog-title"
                onClose={handleAutoRenewOnCloseDialog}
                sx={autoRenewDialogTitleSx}
              >
                Turn On Auto Renew
              </DialogTitle>
              <DialogContent dividers>
                {membershipDetails.defaultPaymentMethod && (
                  <>
                    {membershipDetails.subscription.status !== "trialing" ? (
                      <>
                        <Typography variant="body1" sx={tableTextSx}>
                          By turning on Auto Renew, you&apos;ll be charged{" "}
                          <span>
                            {CurrencySymbol({
                              currency:
                                membershipDetails.defaultPaymentMethod.currency,
                            })}
                            {(
                              membershipDetails.subscription.cost / 100
                            ).toFixed(2)}{" "}
                          </span>{" "}
                          automatically on&nbsp;
                          <span>
                            {dayjs(userProfile.subscriptionExpiryDate).format(
                              "Do MMMM YYYY",
                            )}
                          </span>
                          .
                        </Typography>
                      </>
                    ) : (
                      <Typography variant="body1" sx={tableTextSx}>
                        By turning on Auto Renew, you will resume your{" "}
                        <span>
                          {GetCustomerFacingDisplayName(
                            `${membershipDetails?.productId ?? ""}`,
                          )}
                        </span>{" "}
                        on&nbsp;
                        <span>
                          {dayjs(userProfile.subscriptionExpiryDate).format(
                            "Do MMMM YYYY",
                          )}
                        </span>
                        .
                      </Typography>
                    )}
                  </>
                )}
                <br />
                <Typography variant="body1" sx={tableTextSx}>
                  <span>Do you wish to enable Auto Renew?</span>
                </Typography>
              </DialogContent>
              <DialogActions>
                <Grid container sx={v4GridContainerSx}>
                  <Grid size={{ xs: 12 }} sx={v4GridItemSx}>
                    <Button
                      onClick={handleTurnOnAutoRenew}
                      disabled={updating}
                      sx={dialogButtonSx}
                    >
                      Yes, Turn On
                    </Button>
                    <Button
                      onClick={handleAutoRenewOnCloseDialog}
                      sx={dialogButtonSx}
                      variant="outlined"
                    >
                      No, Go Back
                    </Button>
                  </Grid>
                </Grid>
              </DialogActions>
            </Dialog>
          </>
        )}

      {/* Payment Failed Info Dialog */}
      <Dialog
        fullScreen={fullScreen}
        aria-labelledby="customized-dialog-title"
        open={paymentFailedInfoOpen}
        sx={autoRenewDialogSx}
        onClose={handlePaymentFailedCloseDialog}
      >
        <DialogTitle id="customized-dialog-title" sx={autoRenewDialogTitleSx}>
          Why is my Membership On Hold?
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body1" sx={tableTextSx}>
            Your FarmFLiX Membership is On Hold because payment could not be
            collected.
          </Typography>
          <br />
          <Typography variant="body1" sx={tableTextSx}>
            This can happen for several reasons:
          </Typography>
          <ul>
            <TableTextListItem>
              Your Payment Method may be lacking funds.
            </TableTextListItem>
            <li>Your Payment Method has expired or is no longer valid.</li>
            <li>
              Your Payment Method or Card Issuer did not approve the charge.
            </li>
          </ul>
          <Typography variant="body1" sx={tableTextSx}>
            To maintain your FarmFLiX Membership,{" "}
            <TextLink to="/myaccount/payment-method/update">
              Update your Payment Method
            </TextLink>
            .
          </Typography>
        </DialogContent>
        <DialogActions>
          <Grid container sx={v4GridContainerSx}>
            <Grid size={{ xs: 12 }} sx={v4GridItemSx} align="center">
              <Button
                onClick={handlePaymentFailedCloseDialog}
                sx={dialogButtonSmlSx}
                variant="outlined"
              >
                Close
              </Button>
            </Grid>
          </Grid>
        </DialogActions>
      </Dialog>

      {/* I've already Updated my Payment Method Dialog */}
      <Dialog
        fullScreen={fullScreen}
        aria-labelledby="customized-dialog-title"
        open={paymentAlreadyUpdatedInfoOpen}
        sx={autoRenewDialogSx}
        onClose={handlePaymentAlreadyUpdatedCloseDialog}
      >
        <DialogTitle id="customized-dialog-title" sx={autoRenewDialogTitleSx}>
          Membership Still On Hold?
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body1" sx={tableTextSx}>
            Contact FarmFLiX Support Team at{" "}
            <a style={{ color: "#171717" }} href="mailto:info@farmflix.tv">
              info@farmflix.tv
            </a>{" "}
            for further details regarding your Membership Status.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Grid container sx={v4GridContainerSx}>
            <Grid size={{ xs: 12 }} sx={v4GridItemSx} align="center">
              <Button
                onClick={handlePaymentAlreadyUpdatedCloseDialog}
                sx={dialogButtonSmlSx}
                variant="outlined"
              >
                Close
              </Button>
            </Grid>
          </Grid>
        </DialogActions>
      </Dialog>

      {/* Payment Authentication Modal */}
      <Dialog
        fullScreen={fullScreen}
        aria-labelledby="customized-dialog-title"
        open={paymentAuthStatus.dialogOpen}
        sx={autoRenewDialogSx}
        onClose={handlePaymentAuthCloseDialog}
      >
        <DialogTitle id="customized-dialog-title" sx={autoRenewDialogTitleSx}>
          Payment Authentication
        </DialogTitle>
        <DialogContent dividers>
          {paymentAuthStatus.status === "auth-success" ? (
            <Typography variant="body1" sx={tableTextSx}>
              Your payment has successfully been authenticated.
            </Typography>
          ) : paymentAuthStatus.status === "auth-failure" ? (
            <Typography variant="body1" sx={tableTextSx}>
              We had problems authenticaing your payment. Please try again.
            </Typography>
          ) : paymentAuthStatus.status === "payment-failure" ? (
            <Typography variant="body1" sx={tableTextSx}>
              We had problems processing your payment. Please try again.
            </Typography>
          ) : (
            ""
          )}
        </DialogContent>
        <DialogActions>
          <Grid container sx={v4GridContainerSx}>
            <Grid size={{ xs: 12 }} sx={v4GridItemSx} align="center">
              <Button
                onClick={handlePaymentAuthCloseDialog}
                sx={dialogButtonSmlSx}
                variant="outlined"
              >
                Close
              </Button>
            </Grid>
          </Grid>
        </DialogActions>
      </Dialog>

      {/* Handle Loading */}
      {loading && (
        <Grid container>
          <Grid size={{ xs: 12, sm: 12, md: 12 }}>
            <Box sx={{ m: 5 }} align="center">
              <CircularProgress variant="indeterminate"></CircularProgress>
            </Box>
          </Grid>
        </Grid>
      )}

      {!loading && userProfile && (
        <>
          {/* PARITY RESTORE (guide §2, "`<Grid container>` lost its
              `width: 100%`"): v4's `.MuiGrid-container` declared `width: 100%`
              unconditionally and v9 emits no width rule at all. Overview's own
              root is a Fragment, so this container's rendered parent is
              MyAccount.js's `<Container>` (block) and the restore is a no-op
              today — it is restated because the call site owns that, not this
              file. */}
          <Grid container sx={v4GridContainerWidthSx}>
            <Grid size={{ xs: 12, sm: 12 }}>
              <>
                <Typography variant="h2" sx={myAccountSubTitleSx}>
                  Membership Details
                </Typography>
                {membershipDetails && (
                  <>
                    <MemberStatusBox />
                  </>
                )}
                <Divider sx={sectionDividerSx} />
                <Typography variant="h2" sx={myAccountSubTitleSx}>
                  Personal Details
                </Typography>
                <TableContainer>
                  <Table aria-label="simple table">
                    <TableBody>
                      <TableRow>
                        <TableCell align="left">
                          <Typography variant="body1" sx={tableTextSx}>
                            Name:{" "}
                            <span>
                              {userProfile.firstName} {userProfile.lastName}
                            </span>
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body1" sx={tableTextLinkSx}>
                            <Link to="/myaccount/change-name">
                              {smUp && "Change Name"}
                              {!smUp && <CreateIcon />}
                            </Link>
                          </Typography>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell align="left">
                          <Typography variant="body1" sx={tableTextSx}>
                            Email: <span>{userProfile.email}</span>
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body1" sx={tableTextLinkSx}>
                            <Link to="/myaccount/change-email">
                              {smUp && "Change Email Address"}
                              {!smUp && <CreateIcon />}
                            </Link>
                          </Typography>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell align="left">
                          <Typography variant="body1" sx={tableTextSx}>
                            Password: <span>**********</span>
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body1" sx={tableTextLinkSx}>
                            <Link to="/myaccount/change-password">
                              {smUp && "Change Password"}
                              {!smUp && <CreateIcon />}
                            </Link>
                          </Typography>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell
                          align="left"
                          style={{ borderBottom: "none" }}
                        >
                          <Typography variant="body1" sx={tableTextSx}>
                            Phone:{" "}
                            {userProfile.countryCode &&
                            userProfile.phoneNumber ? (
                              <span>
                                +{userProfile.countryCode}
                                {userProfile.phoneNumber}
                              </span>
                            ) : (
                              <span>Not provided</span>
                            )}
                          </Typography>
                        </TableCell>
                        <TableCell
                          align="right"
                          style={{ borderBottom: "none" }}
                        >
                          <Typography variant="body1" sx={tableTextLinkSx}>
                            {userProfile.countryCode &&
                            userProfile.phoneNumber ? (
                              <Link to="/myaccount/change-phone">
                                {smUp && "Change Phone"}
                                {!smUp && <CreateIcon />}
                              </Link>
                            ) : (
                              <Link to="/myaccount/add-phone">
                                {smUp && "Add Phone"}
                                {!smUp && <AddCircleIcon />}
                              </Link>
                            )}
                          </Typography>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell
                          align="left"
                          style={{ borderBottom: "none" }}
                        >
                          <Typography variant="body1" sx={tableTextSx}>
                            Marketing Preferences
                          </Typography>
                        </TableCell>
                        <TableCell
                          align="right"
                          style={{ borderBottom: "none" }}
                        >
                          <Typography variant="body1" sx={tableTextLinkSx}>
                            <Link to="/myaccount/overview/marketing-prefs">
                              {smUp && "Change Marketing Preferences"}
                              {!smUp && <CreateIcon />}
                            </Link>
                            {marketingPrefsMatch && (
                              <MarketingPreferencesModal />
                            )}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
                <Divider sx={sectionDividerSx} />

                {/* #1261: the "No smartphone or tablet?" card, under the
                    profile table (Ellaie, 4 Nov). It hides itself on phones,
                    tablets and statuses that can't trial. The 24px under it
                    is on the card, not this wrapper, so the wrapper takes no
                    space when the card isn't shown. */}
                <Box sx={{ "& > section": { marginBottom: "24px" } }}>
                  <WebTrialCard
                    memberStatusId={userProfile.memberStatusId}
                    trialOffered={
                      !!subscriptionProducts?.results?.some(
                        (p) => p.memberStatusId === "trial",
                      )
                    }
                    surface="my_account"
                  />
                </Box>

                {/* ============================================================
                    =============== PAYMENT DETAILS + AUTO RENEW ===============
                    ============================================================
                */}
                {membershipDetails && membershipDetails.hasStripeCustomer && (
                  <>
                    {/* <Divider sx={sectionDividerSx} /> */}
                    <Typography variant="h2" sx={myAccountSubTitleSx}>
                      Payment Details
                    </Typography>
                    <TableContainer>
                      <Table aria-label="simple table">
                        <TableBody>
                          <TableRow>
                            <TableCell align="left">
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                }}
                              >
                                {membershipDetails.defaultPaymentMethod ? (
                                  <>
                                    {membershipDetails.defaultPaymentMethod
                                      .cardBrand === "visa" && (
                                      <CardBrandImg
                                        alt={
                                          membershipDetails.defaultPaymentMethod
                                            .cardBrand
                                        }
                                        src={visa}
                                      />
                                    )}
                                    {membershipDetails.defaultPaymentMethod
                                      .cardBrand === "mastercard" && (
                                      <CardBrandImg
                                        alt={
                                          membershipDetails.defaultPaymentMethod
                                            .cardBrand
                                        }
                                        src={mastercard}
                                      />
                                    )}
                                    {membershipDetails.defaultPaymentMethod
                                      .cardBrand === "amex" && (
                                      <CardBrandImg
                                        alt={
                                          membershipDetails.defaultPaymentMethod
                                            .cardBrand
                                        }
                                        src={amex}
                                      />
                                    )}
                                    {membershipDetails.defaultPaymentMethod
                                      .cardBrand === "discover" && (
                                      <CardBrandImg
                                        alt={
                                          membershipDetails.defaultPaymentMethod
                                            .cardBrand
                                        }
                                        src={discover}
                                      />
                                    )}
                                    <Typography
                                      variant="body1"
                                      sx={tableTextSx}
                                    >
                                      <span>
                                        **** **** ****{" "}
                                        {
                                          membershipDetails.defaultPaymentMethod
                                            .last4
                                        }
                                      </span>
                                      <br />
                                      <CardExpirySpan>
                                        Exp:{" "}
                                        {
                                          membershipDetails.defaultPaymentMethod
                                            .exp.month
                                        }{" "}
                                        /{" "}
                                        {
                                          membershipDetails.defaultPaymentMethod
                                            .exp.year
                                        }
                                      </CardExpirySpan>
                                    </Typography>
                                  </>
                                ) : (
                                  <Typography variant="body1" sx={tableTextSx}>
                                    <span>No Payment Method</span>
                                  </Typography>
                                )}
                              </div>
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="body1" sx={tableTextLinkSx}>
                                {membershipDetails.defaultPaymentMethod ? (
                                  <>
                                    <Link to="/myaccount/payment-method/update">
                                      {smUp && "Update"}
                                      {!smUp && <CreateIcon />}
                                    </Link>
                                    {smUp && (
                                      <span style={{ color: "#171717" }}>
                                        &nbsp; / &nbsp;
                                      </span>
                                    )}
                                    <Link to="/myaccount/payment-method/remove">
                                      {smUp && "Remove"}
                                      {!smUp && (
                                        <RemoveCircleIcon
                                          style={{
                                            marginLeft: "15px",
                                            color: "#c53737",
                                          }}
                                        />
                                      )}
                                    </Link>
                                  </>
                                ) : (
                                  <Link to="/myaccount/payment-method/add">
                                    {smUp && "Add Payment Method"}
                                    {!smUp && <AddCircleIcon />}
                                  </Link>
                                )}
                              </Typography>
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            {membershipDetails.subscription &&
                              !membershipDetails.subscription
                                .cancelAtPeriodEnd && (
                                <>
                                  <TableCell align="left">
                                    {membershipDetails.subscription
                                      .nextPayment && (
                                      <Typography
                                        variant="body1"
                                        sx={tableTextSx}
                                      >
                                        Next Payment:{" "}
                                        {/* <span> - SMALL BUG HERE - cost showing '0' when subscription in trialling
                                          {CurrencySymbol({
                                            currency: membershipDetails.stripeCustomerCurrency
                                          })}
                                          {membershipDetails.subscription.cost}
                                        </span>{" "} */}
                                        <span>
                                          {dayjs(
                                            membershipDetails.subscription
                                              .nextPayment,
                                          ).format("Do MMMM YYYY")}
                                        </span>
                                      </Typography>
                                    )}
                                  </TableCell>
                                  <TableCell align="right">
                                    <Typography
                                      variant="body1"
                                      sx={tableTextLinkSx}
                                    >
                                      <Link to="/myaccount/payment-history">
                                        Payment History
                                      </Link>
                                    </Typography>
                                  </TableCell>
                                </>
                              )}
                          </TableRow>
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </>
                )}
                {membershipDetails && (
                  <TableContainer>
                    <Table>
                      <TableBody>
                        <TableRow>
                          <TableCell align="left">
                            {(membershipDetails.memberStatusId === "standard" ||
                              membershipDetails.memberStatusId ===
                                "standard-expired") && (
                              <>
                                {membershipDetails.hasStripeCustomer ? (
                                  <>
                                    {membershipDetails.hasStripeSubscription ? (
                                      <Typography
                                        variant="body1"
                                        sx={tableTextSx}
                                      >
                                        Auto Renew is currently{" "}
                                        <b>
                                          {!membershipDetails.subscription
                                            .cancelAtPeriodEnd
                                            ? "On"
                                            : "Off"}
                                        </b>
                                        .
                                      </Typography>
                                    ) : (
                                      <Typography
                                        variant="body1"
                                        sx={tableTextSx}
                                      >
                                        Auto Renew is not set up.
                                      </Typography>
                                    )}
                                  </>
                                ) : (
                                  <Typography variant="body1" sx={tableTextSx}>
                                    Auto Renew is not set up.
                                  </Typography>
                                )}
                              </>
                            )}

                            {/* Trial User */}
                            {(membershipDetails.memberStatusId === "trial" ||
                              membershipDetails.memberStatusId ===
                                "trial-expired") && (
                              <Typography variant="body1" sx={tableTextSx}>
                                Join Now and set up Auto Renew.
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell align="right">
                            {(membershipDetails.memberStatusId === "standard" ||
                              membershipDetails.memberStatusId ===
                                "standard-expired") && (
                              <>
                                {membershipDetails.hasStripeCustomer &&
                                membershipDetails.hasStripeSubscription ? (
                                  <FormControlLabel
                                    style={{ marginRight: "0px" }}
                                    sx={autoRenewSwitchSx}
                                    control={
                                      <Switch
                                        checked={
                                          !membershipDetails.subscription
                                            .cancelAtPeriodEnd
                                        }
                                        onChange={handleAutoRenewSwitch}
                                        name="checkedB"
                                        color="primary"
                                      />
                                    }
                                  />
                                ) : (
                                  <Typography
                                    variant="body1"
                                    sx={tableTextLinkSx}
                                  >
                                    <Link to="/myaccount/join-now">
                                      Set up Auto Renew
                                    </Link>
                                  </Typography>
                                )}
                              </>
                            )}

                            {/* Trial User */}
                            {(membershipDetails.memberStatusId === "trial" ||
                              membershipDetails.memberStatusId ===
                                "trial-expired") && (
                              <Typography variant="body1" sx={tableTextLinkSx}>
                                <Link to="/myaccount/join-now">
                                  Set up Auto Renew
                                </Link>
                              </Typography>
                            )}
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}

                <Divider sx={sectionDividerSx} />

                <Typography variant="body2" sx={darkBodyTextSx}>
                  <span>Need Help with your FarmFLiX Account?</span>
                </Typography>
                <Typography variant="body2" sx={darkBodyTextSx}>
                  Use the FarmFLiX Help Widget to get in touch or email us
                  directly at:{" "}
                  <a href="mailto:info@farmflix.tv">info@farmflix.tv</a>
                </Typography>
              </>
            </Grid>
          </Grid>
        </>
      )}
    </>
  );
}
